import { useEffect, useMemo, useState } from "react";
import { CreditCard, Minus, Plus, ShieldCheck, Tag, Ticket } from "lucide-react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { customerHeaders, getCustomerToken } from "@/lib/customerAuth";

const initialBuyer = { buyer_name: "", buyer_email: "", buyer_phone: "" };
const SERVICE_FEE_FALLBACK = 40;

export default function Tickets() {
  const [searchParams] = useSearchParams();
  const [settings, setSettings] = useState(null);
  const [selectedId, setSelectedId] = useState("");
  const [quantity, setQuantity] = useState(1);
  const [buyer, setBuyer] = useState(initialBuyer);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [discountCode, setDiscountCode] = useState("");
  const [appliedDiscount, setAppliedDiscount] = useState(null);
  const [discountLoading, setDiscountLoading] = useState(false);
  const [discountMessage, setDiscountMessage] = useState("");
  const appliedDiscountCode = appliedDiscount?.code || "";

  useEffect(() => {
    api.get("/tickets/settings")
      .then((response) => {
        setSettings(response.data);
        const first = response.data.ticket_types.find((item) => item.active && item.available > 0);
        setSelectedId(first?.id || "");
      })
      .catch(() => setError("No se pudo cargar la venta de boletos."))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (!getCustomerToken()) return;
    api.get("/auth/me", { headers: customerHeaders() })
      .then((response) => setBuyer({
        buyer_name: response.data.name || "",
        buyer_email: response.data.email || "",
        buyer_phone: response.data.phone || "",
      }))
      .catch(() => {});
  }, []);

  const selected = useMemo(
    () => settings?.ticket_types.find((item) => item.id === selectedId),
    [settings, selectedId]
  );
  const maxQuantity = Math.max(1, Math.min(selected?.max_per_order || 1, selected?.available || 1));
  const serviceFeeUnit = settings?.service_fee_mxn ?? SERVICE_FEE_FALLBACK;
  const subtotal = (selected?.price_mxn || 0) * quantity;
  const discount = appliedDiscount?.discount_mxn || 0;
  const serviceFee = serviceFeeUnit * quantity;
  const total = subtotal - discount + serviceFee;

  useEffect(() => {
    if (quantity > maxQuantity) setQuantity(maxQuantity);
  }, [maxQuantity, quantity]);

  useEffect(() => {
    const code = appliedDiscountCode;
    if (!code || !selectedId) return undefined;
    let cancelled = false;
    setDiscountLoading(true);
    api.post("/tickets/discounts/validate", {
      code,
      ticket_type_id: selectedId,
      quantity,
    }).then((response) => {
      if (!cancelled) {
        setAppliedDiscount(response.data);
        setDiscountMessage(`${response.data.label} aplicado.`);
        setDiscountLoading(false);
      }
    }).catch((requestError) => {
      if (!cancelled) {
        setAppliedDiscount(null);
        setDiscountMessage(requestError.response?.data?.detail || "El código ya no aplica a esta cantidad.");
        setDiscountLoading(false);
      }
    });
    return () => { cancelled = true; };
  }, [appliedDiscountCode, quantity, selectedId]);

  const applyDiscount = async () => {
    if (!discountCode.trim() || !selected) return;
    setDiscountLoading(true);
    setDiscountMessage("");
    try {
      const response = await api.post("/tickets/discounts/validate", {
        code: discountCode,
        ticket_type_id: selected.id,
        quantity,
      });
      setAppliedDiscount(response.data);
      setDiscountCode(response.data.code);
      setDiscountMessage(`${response.data.label} aplicado.`);
    } catch (requestError) {
      setAppliedDiscount(null);
      setDiscountMessage(requestError.response?.data?.detail || "El código no es válido.");
    } finally {
      setDiscountLoading(false);
    }
  };

  const checkout = async (event) => {
    event.preventDefault();
    if (!selected) return;
    setSubmitting(true);
    setError("");
    try {
      const response = await api.post(
        "/tickets/checkout",
        { ticket_type_id: selected.id, quantity, discount_code: appliedDiscount?.code || "", ...buyer },
        { headers: customerHeaders() }
      );
      window.location.assign(response.data.checkout_url);
    } catch (requestError) {
      setError(requestError.response?.data?.detail || "No se pudo iniciar el pago.");
      setSubmitting(false);
    }
  };

  if (loading) {
    return <div className="section-pad container-cg text-sm text-[var(--brand-muted)]">Cargando boletos...</div>;
  }

  return (
    <div className="section-pad">
      <div className="container-cg">
        <div className="overline text-[var(--brand-primary)]">Boletaje oficial</div>
        <h1 className="font-serif-cg text-4xl sm:text-5xl lg:text-6xl tracking-tight mt-3">Compra tus accesos.</h1>
        <p className="mt-4 max-w-2xl text-[var(--brand-muted)] leading-relaxed">
          Elige tus boletos, paga de forma segura con Stripe y recibe un código QR único para ingresar al evento.
        </p>

        {searchParams.get("cancelado") ? (
          <div className="mt-8 border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900">
            El pago fue cancelado. Tu tarjeta no fue cobrada y puedes intentarlo nuevamente.
          </div>
        ) : null}

        {!settings?.sales_enabled ? (
          <div className="mt-10 card-cg p-8 text-center">
            <Ticket className="mx-auto text-[var(--brand-primary)]" size={30} />
            <h2 className="font-serif-cg text-2xl mt-4">Venta temporalmente pausada</h2>
            <p className="text-sm text-[var(--brand-muted)] mt-2">Vuelve más tarde para consultar disponibilidad.</p>
          </div>
        ) : (
          <form onSubmit={checkout} className="grid lg:grid-cols-[1.1fr_0.9fr] gap-8 mt-10 items-start">
            <div className="space-y-4">
              {settings?.ticket_types.filter((item) => item.active).map((item) => {
                const selectedTicket = selectedId === item.id;
                return (
                  <button
                    key={item.id}
                    type="button"
                    disabled={item.available < 1}
                    onClick={() => { setSelectedId(item.id); setQuantity(1); }}
                    className={`w-full text-left border p-5 md:p-6 transition-colors disabled:opacity-50 ${
                      selectedTicket
                        ? "border-[var(--brand-primary)] bg-white"
                        : "border-[var(--brand-border)] bg-[var(--brand-surface)] hover:border-[var(--brand-primary)]"
                    }`}
                  >
                    <div className="flex items-start justify-between gap-5">
                      <div>
                        <h2 className="font-serif-cg text-2xl">{item.name}</h2>
                        <p className="text-sm text-[var(--brand-muted)] mt-2">{item.description}</p>
                        <p className="overline text-[var(--brand-muted)] mt-4">{item.available} disponibles</p>
                      </div>
                      <div className="text-right whitespace-nowrap">
                        <div className="font-serif-cg text-2xl">${item.price_mxn.toFixed(2)}</div>
                        <div className="text-xs text-[var(--brand-muted)] mt-1">+ ${serviceFeeUnit.toFixed(2)} servicio</div>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>

            <div className="card-cg bg-white p-6 md:p-8 lg:sticky lg:top-28">
              <h2 className="font-serif-cg text-2xl">Datos de compra</h2>
              <div className="mt-6 space-y-4">
                <label className="block">
                  <span className="overline text-[var(--brand-muted)]">Nombre completo</span>
                  <input required value={buyer.buyer_name} onChange={(e) => setBuyer({ ...buyer, buyer_name: e.target.value })} className="mt-2 w-full border border-[var(--brand-border)] px-3 py-3 outline-none focus:border-[var(--brand-primary)]" />
                </label>
                <label className="block">
                  <span className="overline text-[var(--brand-muted)]">Correo electrónico</span>
                  <input required type="email" autoComplete="email" value={buyer.buyer_email} onChange={(e) => setBuyer({ ...buyer, buyer_email: e.target.value })} className="mt-2 w-full border border-[var(--brand-border)] px-3 py-3 outline-none focus:border-[var(--brand-primary)]" />
                </label>
                <label className="block">
                  <span className="overline text-[var(--brand-muted)]">Teléfono celular</span>
                  <input required type="tel" autoComplete="tel" minLength={7} placeholder="477 000 0000" value={buyer.buyer_phone} onChange={(e) => setBuyer({ ...buyer, buyer_phone: e.target.value })} className="mt-2 w-full border border-[var(--brand-border)] px-3 py-3 outline-none focus:border-[var(--brand-primary)]" />
                </label>
                <p className="text-xs text-[var(--brand-muted)]">Enviaremos los QR y la confirmación al correo capturado.</p>

                <div>
                  <span className="overline text-[var(--brand-muted)]">Cantidad</span>
                  <div className="mt-2 flex items-center border border-[var(--brand-border)] w-fit">
                    <button type="button" onClick={() => setQuantity((value) => Math.max(1, value - 1))} className="h-11 w-11 inline-flex items-center justify-center" aria-label="Restar boleto"><Minus size={16} /></button>
                    <span className="w-12 text-center font-semibold">{quantity}</span>
                    <button type="button" onClick={() => setQuantity((value) => Math.min(maxQuantity, value + 1))} className="h-11 w-11 inline-flex items-center justify-center" aria-label="Agregar boleto"><Plus size={16} /></button>
                  </div>
                </div>

                <div>
                  <span className="overline text-[var(--brand-muted)]">Código de descuento</span>
                  <div className="mt-2 flex">
                    <div className="relative flex-1">
                      <Tag size={16} className="absolute left-3 top-3.5 text-[var(--brand-muted)]" />
                      <input
                        value={discountCode}
                        onChange={(event) => {
                          const value = event.target.value.toUpperCase().replace(/[^A-Z0-9-]/g, "");
                          setDiscountCode(value);
                          if (appliedDiscount?.code !== value) setAppliedDiscount(null);
                          setDiscountMessage("");
                        }}
                        onKeyDown={(event) => {
                          if (event.key === "Enter") { event.preventDefault(); applyDiscount(); }
                        }}
                        placeholder="EJEMPLO10"
                        maxLength={24}
                        className="w-full border border-[var(--brand-border)] py-3 pl-10 pr-3 uppercase outline-none focus:border-[var(--brand-primary)]"
                      />
                    </div>
                    <button type="button" onClick={applyDiscount} disabled={discountLoading || !discountCode} className="border border-l-0 border-[var(--brand-primary)] px-4 text-sm font-semibold text-[var(--brand-primary)] disabled:opacity-50">
                      {discountLoading ? "Validando..." : "Aplicar"}
                    </button>
                  </div>
                  {discountMessage ? <p className={`mt-2 text-xs ${appliedDiscount ? "text-green-700" : "text-red-700"}`}>{discountMessage}</p> : null}
                </div>

                <div className="border-t border-[var(--brand-border)] pt-5 space-y-2">
                  <div className="flex justify-between gap-4 text-sm"><span className="text-[var(--brand-muted)]">Subtotal de boletos</span><span>${subtotal.toFixed(2)}</span></div>
                  {discount > 0 ? <div className="flex justify-between gap-4 text-sm text-green-700"><span>Descuento {appliedDiscount.code}</span><span>-${discount.toFixed(2)}</span></div> : null}
                  <div className="flex justify-between gap-4 text-sm"><span className="text-[var(--brand-muted)]">Cargo por servicio (${serviceFeeUnit.toFixed(2)} × {quantity})</span><span>${serviceFee.toFixed(2)}</span></div>
                  <div className="flex items-end justify-between gap-4 border-t border-[var(--brand-border)] pt-3">
                    <span className="text-sm font-semibold">Total</span>
                    <strong className="font-serif-cg text-3xl">${total.toFixed(2)} MXN</strong>
                  </div>
                </div>

                {error ? <div className="border border-red-200 bg-red-50 px-3 py-3 text-sm text-red-700">{error}</div> : null}

                <button type="submit" disabled={!selected || submitting || discountLoading || !settings?.stripe_configured || !settings?.stripe_webhook_configured || !settings?.ticket_security_configured} className="btn-primary-cg w-full justify-center disabled:opacity-50">
                  <CreditCard size={17} /> {submitting ? "Abriendo pago..." : "Pagar con Stripe"}
                </button>
                {!settings?.stripe_configured ? <p className="text-xs text-amber-700">El administrador todavía debe conectar Stripe en Render.</p> : null}
                {!settings?.stripe_webhook_configured ? <p className="text-xs text-amber-700">Falta configurar el webhook de Stripe en Render.</p> : null}
                {!settings?.ticket_security_configured ? <p className="text-xs text-amber-700">Falta configurar la firma segura de los boletos en Render.</p> : null}
                <p className="flex items-start gap-2 text-xs text-[var(--brand-muted)] leading-relaxed">
                  <ShieldCheck size={15} className="shrink-0 mt-0.5" /> El pago se procesa en Stripe. Cuchara Grande no almacena los datos de tu tarjeta.
                </p>
              </div>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
